import { useEffect, useRef, lazy, Suspense, type ReactNode } from 'react';
import { Helmet } from 'react-helmet';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useDevTools } from '@/hooks/useDevTools';
import Header from '@/shared/layout/Header';
import Footer from '@/shared/layout/Footer';
import Sidebar, { MobileTabBar, MobileTopBar } from '@/shared/layout/Sidebar';
import { shouldShowAppShell, ownsItsChrome } from '@/shared/layout/appShell';
import Landing from '@/pages/Landing';
// Statically imported (not lazy): Landing.tsx already pulls this in eagerly since
// it's the diagnostic engine behind '/' too, so lazy-splitting it for /try alone
// bought nothing and produced a "dynamic import also statically imported" warning.
import TryAQuestion from '@/pages/TryAQuestion';
import { SmartAppBanner } from '@/shared/components/SmartAppBanner';
import PassPlanDownload from '@/pages/PassPlanDownload';
import ExamReport from '@/pages/ExamReport';
import Blog from '@/pages/Blog';
import BlogPost from '@/pages/BlogPost';
import SignIn from '@/pages/SignIn';
import { diagnosticVariant } from '@/utils/diagnosticVariant';
import { DiagnosticPrerender, PublicDiagnosticShell, TRY_DESCRIPTION, TRY_TITLE } from '@/features/publicDiagnostic';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import Disclosures from '@/pages/Disclosures';
import TermsOfService from '@/pages/TermsOfService';
import DeleteAccount from '@/pages/DeleteAccount';
import { CaseworkPrivacy, CaseworkSupport } from '@/pages/CaseworkApp';
import AboutUs from '@/pages/AboutUs';
import { SpeedInsights } from "@vercel/speed-insights/react"
import { isProductionHost } from '@/utils/productionHost';
import { trackSpaPageView } from '@/utils/Analytics';
import NotFound from '@/pages/NotFound';
import ForgotPassword from '@/pages/ForgotPassword';
import PrivateRoute from '@/shared/components/PrivateRoute';
import CelebrationOverlay from '@/shared/components/CelebrationOverlay';
import { initializeErrorTracking } from '@/utils/ErrorTracking';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import ErrorBoundary from '@/shared/components/ErrorBoundary';
import DevStudentView from '@/shared/components/DevStudentView';
import AuthLoadingWrapper from '@/shared/components/AuthLoadingWrapper';
import { Toaster } from 'react-hot-toast';
import ReplayConsentBar from '@/components/ReplayConsentBar';
import EmailVerificationHandler from '@/shared/components/EmailVerificationHandler';
import EmailRedirectHandler from '@/shared/components/EmailRedirectHandler';
import UnsubscribePage from '@/pages/UnsubscribePage';
import ReactivatePage from '@/pages/ReactivatePage';
import TrialFeedbackPage from '@/pages/TrialFeedbackPage';
import ExcludeDevice from '@/pages/ExcludeDevice';
import { useEmailLinkHandler } from '@/hooks/useEmailLinkHandler';
import { useSocialRedirectCompletion } from '@/hooks/useSocialRedirectCompletion';
import { captureEmailAttribution, captureAttributionIfPresent } from '@/utils/emailAttribution';
import { rememberAdDoor } from '@/utils/learnerContext';
import { captureReferralCode } from '@/utils/referralTracking';
import { clearExpiredItems } from '@/utils/localStorageWithTTL';
import QuizLoadingRoute from '@/shared/components/QuizLoadingRoute';
import NotificationSystem from '@/shared/components/NotificationSystem';
import OnboardingPage from '@/pages/OnboardingPage';
import AccountSetupPage from '@/pages/AccountSetupPage';
import EmailVerificationPending from '@/shared/components/EmailVerificationPending';
import VerifyEmail from '@/pages/VerifyEmail';
import LandingLBSW from '@/pages/LandingLBSW';
import AffiliateSignup from '@/pages/AffiliateSignup';
import AffiliateSignIn from '@/pages/AffiliateSignIn';
import TestimonialProgram from '@/pages/TestimonialProgram';
import BrandedLoader from '@/shared/components/BrandedLoader';
import { OrganizationSchema } from '@/seo/schema';

// A single-page app keeps the scroll position across route changes, so a link
// tapped at the bottom of one page opened the next page already scrolled down
// (Jordan, 2026-09-09). Scroll to the top on every pathname change; leave
// hash links alone so in-page anchors still work.
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

// The first render is the full page load, which GA4 already counts.
function SpaPageView() {
  const { pathname } = useLocation();
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    trackSpaPageView();
  }, [pathname]);
  return null;
}


// Lazy load heavy routes for better initial bundle size
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const QuizApp = lazy(() => import('@/pages/QuizApp'));
const QuizSummaryPage = lazy(() => import('@/pages/QuizSummaryPage'));
const AskPam = lazy(() => import('@/pages/AskPam'));
const SimScenarioSelection = lazy(() => import('@/features/sims/components/SimScenarioSelection'));
const SimSession = lazy(() => import('@/features/sims/components/SimSession'));
const AdminDashboard = lazy(() => import('@/pages/AdminDashboard'));
const ReviewBatchPage = lazy(() => import('@/pages/ReviewBatchPage'));
const FlashcardsPage = lazy(() => import('@/pages/FlashcardsPage'));
const ProgressPage = lazy(() => import('@/pages/ProgressPage'));
const AffiliateDashboard = lazy(() => import('@/pages/AffiliateDashboard'));
const Onboarding = lazy(() => import('@/pages/Onboarding'));
const ExamDatePage = lazy(() => import('@/pages/ExamDatePage'));
const PricingPage = lazy(() => import('@/pages/Pricing'));
const CheckoutPage = lazy(() => import('@/pages/Checkout'));
const ComparePage = lazy(() => import('@/pages/Compare'));
const UserSettings = lazy(() => import('@/pages/UserSettings'));
const Feedback = lazy(() => import('@/pages/Feedback'));
const FinishSignIn = lazy(() => import('@/pages/FinishSignIn'));
const ExamResultsPage = lazy(() => import('@/pages/ExamResultsPage'));
const PaymentSuccessRedirect = lazy(() => import('@/pages/PaymentSuccessRedirect'));
const AppPaymentSuccess = lazy(() => import('@/pages/AppPaymentSuccess'));
const AppPaymentCancelled = lazy(() => import('@/pages/AppPaymentCancelled'));
const QuestionsViewer = lazy(() => import('@/pages/QuestionsViewer').then(m => ({ default: m.QuestionsViewer })));
const StartQuizRedirect = lazy(() => import('@/pages/StartQuizRedirect'));
const EmailAnswerResult = lazy(() => import('@/pages/EmailAnswerResult'));
const ExamPreparednessPage = lazy(() => import('@/pages/ExamPreparednessPage'));
const Practice = lazy(() => import('@/pages/Practice'));
const TrialDashboardPreviewPage = lazy(() => import('@/pages/TrialDashboardPreviewPage'));
// Lazy like /pricing (also prerendered): the prerender's pageHandler polls for
// the route's real <title>/canonical, which rides out the lazy chunk load, so
// paid-search-only pages stay out of the main bundle every app user downloads.
const RetakerLanding = lazy(() => import('@/pages/RetakerLanding'));
// Public licensing pages: prerendered marketing routes, lazy so the bundled
// rulebook JSON never lands in the main chunk.
const LicensingIndex = lazy(() => import('@/pages/LicensingIndex'));
const LicensingState = lazy(() => import('@/pages/LicensingState'));
const HoursWaitlist = lazy(() => import('@/pages/HoursWaitlist'));
// Public program pages: same deal, lazy so the school index JSON never
// lands in the main chunk (and the per-school records never land at all).
const ProgramsIndex = lazy(() => import('@/pages/ProgramsIndex'));
const ProgramSchool = lazy(() => import('@/pages/ProgramSchool'));

// Create a QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime)
      retry: (failureCount, error) => {
        // Don't retry for auth errors or 4xx errors
        if (error && typeof error === 'object' && 'code' in error) {
          const code = (error as { code?: string }).code;
          if (code?.includes('auth') || code?.includes('permission')) {
            return false;
          }
        }
        return failureCount < 3;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

/**
 * /trial-signup is retired (GA4: 13 views/30d vs 287 on /try; the legacy
 * account-first flow whose headline contradicted the brand line). vercel.json
 * also 301s it server-side for crawlers/non-JS fetches (matching how /lcsw
 * and /lmsw were retired); this client-side redirect covers in-app navigation
 * and preserves `location.search` so UTM/attribution and `?exam=` survive.
 */
const TrialSignupRedirect = () => {
  const location = useLocation();
  return <Navigate to={`/try${location.search}`} replace />;
};

/**
 * AppChrome — chooses the shell for the current route.
 *
 * Logged-in app routes (dashboard, practice, sims, progress, ask-pam,
 * flashcards, settings, …) render the .pen left-sidebar + mobile bottom bar and
 * NO Header/Footer. The quiz player (/quiz/…) is full-screen — its own .pen top
 * bar, no shell and no marketing chrome. Everything else (landing, about, blog,
 * pricing/paywall, sign-in, sign-up, onboarding, legal) keeps the marketing
 * Header + Footer. Gating is route-based (see shouldShowAppShell)
 * AND requires an authenticated user, so a logged-out visitor never sees the
 * app shell before PrivateRoute redirects them.
 */
const AppChrome = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const { currentUser } = useAuth();
  const showShell = shouldShowAppShell(location.pathname) && !!currentUser;

  // Full-bleed routes own their entire chrome and must NOT be wrapped in the
  // sidebar shell OR the marketing Header/Footer: the quiz player (its own .pen
  // top bar) and the .pen two-panel sign-in (edge-to-edge teal panel + form).
  // Otherwise the marketing top nav sits on top of a screen meant to fill the viewport.
  if (ownsItsChrome(location.pathname)) {
    return <div className="min-h-screen bg-neutral-50">{children}</div>;
  }

  if (showShell) {
    return (
      <div className="flex h-screen overflow-hidden bg-neutral-50">
        {/* App shell: fixed viewport height, the MAIN scrolls internally (Gmail/Slack
            pattern) so the sidebar footer stays pinned to the viewport bottom and
            content never gets clipped by any window-scroll lock. */}
        <Sidebar />
        <MobileTopBar />
        <main className="flex-1 min-w-0 overflow-y-auto pt-14 lg:pt-0 pb-16 lg:pb-0">{children}</main>
        <MobileTabBar />
        {/* Journey celebrations take over the screen once, ever, per milestone */}
        <CelebrationOverlay />
      </div>
    );
  }

  return (
    <div className="main-container bg-neutral-100">
      <Header />
      <main className="main-content bg-neutral-100">{children}</main>
      <Footer />
    </div>
  );
};

const AppContent = () => {
  const {
    isDevMode,
    isStudentViewActive,
    activateStudentView,
    deactivateStudentView
  } = useAuth();

  // Process deep links from marketing emails on navigation
  useEmailLinkHandler();

  // Complete any signInWithRedirect() that just returned, wherever the user
  // landed (Safari / iOS-WebViews / in-app browsers). No-op when nothing is
  // pending; cedes to TrialSignupFlow when a signup redirect is in flight.
  useSocialRedirectCompletion();

  return (
    <AuthLoadingWrapper>
      <AppChrome>
        {/* Default description + share/OG tags for every route. These live HERE
            (not in index.html) so page-level Helmets OVERRIDE rather than
            duplicate: scrapers take the FIRST og:image, static base tags in
            index.html were shadowing every page-specific card, and a static
            <meta name="description"> shipped alongside Helmet's own left every
            page with two conflicting descriptions. og:title is kept in sync with
            index.html's fallback <title> (the site name is carried separately by
            og:site_name). */}
        <Helmet>
          <meta
            name="description"
            content="Adaptive ASWB exam prep in the new 2026 format, with every question reviewed by Pamela Coin, LCSW. Free diagnostic, free Pass Plan workbook. Built by social workers for LCSW, LMSW, and LBSW candidates."
          />
          <meta property="og:title" content="Pass the 2026 ASWB Exam" />
          <meta property="og:description" content="Adaptive practice in the new 3-choice format, reviewed by Pam Coin, LCSW and Board Approved Clinical Supervisor. Free diagnostic, no account." />
          <meta property="og:type" content="website" />
          <meta property="og:site_name" content="Social Work Test Prep Academy" />
          <meta property="og:image" content="https://www.socialworktestprepacademy.com/images/default-og-image.png" />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:image" content="https://www.socialworktestprepacademy.com/images/default-og-image.png" />
        </Helmet>
        {/* Site-wide EducationalOrganization JSON-LD (founder = LCSW → E-E-A-T). */}
        <OrganizationSchema />
        <ScrollToTop />
        <SpaPageView />
        <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/lbsw" element={<LandingLBSW />} />
            {/* Retaker intent-matched landing (paid search + SEO pull motion) */}
            <Route path="/retake" element={
              <Suspense fallback={<BrandedLoader variant="dashboard" />}>
                <RetakerLanding />
              </Suspense>
            } />
            {/* /lcsw + /lmsw retired as landing pages (0 GA4 sessions/30d, ~95%
                template-identical to /lbsw). Redirect, don't 404 — may have
                inbound paid-search/backlinks. Mirrors the /exam-prep pattern below. */}
            {/* Public licensing pages (#132): /licensing and /licensing/:abbr.
                Two-letter LOWERCASE is canonical; LicensingState redirects any
                other casing to it so /licensing/VA and /licensing/va are never
                two indexable URLs for one page. */}
            <Route path="/licensing" element={
              <Suspense fallback={<BrandedLoader variant="dashboard" />}>
                <LicensingIndex />
              </Suspense>
            } />
            <Route path="/hours" element={
              <Suspense fallback={<BrandedLoader variant="dashboard" />}>
                <HoursWaitlist />
              </Suspense>
            } />
            <Route path="/licensing/:abbr" element={
              <Suspense fallback={<BrandedLoader variant="dashboard" />}>
                <LicensingState />
              </Suspense>
            } />
            {/* Public program pages (#473): /programs and /programs/:state/:slug.
                Lowercase state is canonical; ProgramSchool redirects any other
                casing to it so one school is never two indexable URLs. */}
            <Route path="/programs" element={
              <Suspense fallback={<BrandedLoader variant="dashboard" />}>
                <ProgramsIndex />
              </Suspense>
            } />
            <Route path="/programs/:state/:slug" element={
              <Suspense fallback={<BrandedLoader variant="dashboard" />}>
                <ProgramSchool />
              </Suspense>
            } />
            <Route path="/lmsw" element={<Navigate to="/" replace />} />
            <Route path="/lcsw" element={<Navigate to="/" replace />} />
            {/* Development routes - Development only */}
            {import.meta.env.DEV && (
              <>
                <Route path="/questions-viewer" element={
                  <Suspense fallback={<BrandedLoader variant="dashboard" />}>
                    <QuestionsViewer />
                  </Suspense>
                } />
              </>
            )}
            <Route
              path="/quiz/loading"
              element={
                <PrivateRoute>
                  <QuizLoadingRoute />
                </PrivateRoute>
              }
            />
            <Route
              path="/quiz/new"
              element={
                <PrivateRoute>
                  <Suspense fallback={<BrandedLoader variant="dashboard" />}>
                    <QuizApp />
                  </Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="/quiz/:quizId"
              element={
                <PrivateRoute>
                  <Suspense fallback={<BrandedLoader variant="dashboard" />}>
                    <QuizApp />
                  </Suspense>
                </PrivateRoute>
              }
            />
            <Route
              path="/quiz/:quizId/summary"
              element={
                <PrivateRoute>
                  <Suspense fallback={<BrandedLoader variant="dashboard" />}>
                    <QuizSummaryPage />
                  </Suspense>
                </PrivateRoute>
              }
            />
            <Route path="/finish-signin" element={
              <Suspense fallback={<BrandedLoader variant="dashboard" />}>
                <FinishSignIn />
              </Suspense>
            } />
            <Route path="/signin" element={<SignIn />} />
            <Route path="/start" element={
              <Suspense fallback={<BrandedLoader variant="dashboard" />}>
                <StartQuizRedirect />
              </Suspense>
            } />
            {/* Static answer-in-email A/B result page (public — grades from URL) */}
            <Route path="/q" element={
              <Suspense fallback={<BrandedLoader variant="dashboard" />}>
                <EmailAnswerResult />
              </Suspense>
            } />
            {/* Public "try a free ASWB question" lead magnet (marketing destination) */}
            <Route path="/pass-plan" element={<PassPlanDownload />} />
            {/* Public post-exam collector — primary data for the aggregate 2026 report */}
            <Route path="/exam-report" element={<ExamReport />} />
            <Route path="/try" element={
              <Suspense fallback={<BrandedLoader variant="dashboard" />}>
                <SmartAppBanner />
                {diagnosticVariant() === null ? (
                  <DiagnosticPrerender canonicalPath="/try" introPageTitle={TRY_TITLE} metaDescription={TRY_DESCRIPTION} />
                ) : diagnosticVariant() === 'six_turn' ? (
                  <PublicDiagnosticShell />
                ) : (
                  <TryAQuestion />
                )}
              </Suspense>
            } />
            {/* Retired: legacy account-first signup page. See TrialSignupRedirect above. */}
            <Route path="/trial-signup" element={<TrialSignupRedirect />} />
            <Route path="/email-verification-pending" element={<EmailVerificationPending />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/trial-setup" element={<AccountSetupPage />} />
            <Route path="/account-setup" element={<AccountSetupPage />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/email-action" element={<EmailVerificationHandler />} />
            <Route path="/auth/email-redirect" element={<EmailRedirectHandler />} />
            <Route path="/unsubscribe" element={<UnsubscribePage />} />
            <Route path="/reactivate" element={<ReactivatePage />} />
            {/* One-tap "what is in the way" answer from the last-day trial letter (public; the signed link is the credential) */}
            <Route path="/trial-feedback" element={<TrialFeedbackPage />} />
            {/* One-tap "exclude this device from analytics" — no URL params to remember.
                See src/pages/ExcludeDevice.tsx for why this exists (Pam tests logged out). */}
            <Route path="/exclude-me" element={<ExcludeDevice />} />
            <Route path="/testimonial-program" element={<TestimonialProgram />} />
            <Route path="/exam-results" element={
              <PrivateRoute>
                <Suspense fallback={<BrandedLoader variant="dashboard" />}>
                  <ExamResultsPage />
                </Suspense>
              </PrivateRoute>
            } />
            <Route path="/feedback" element={
              <PrivateRoute>
                <Suspense fallback={<BrandedLoader variant="dashboard" />}>
                  <Feedback />
                </Suspense>
              </PrivateRoute>
            } />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/pricing" element={
              <Suspense fallback={<BrandedLoader variant="dashboard" />}>
                <PricingPage />
              </Suspense>
            } />
            <Route path="/checkout" element={
              <Suspense fallback={<BrandedLoader variant="dashboard" />}>
                <CheckoutPage />
              </Suspense>
            } />
            <Route path="/compare" element={
              <Suspense fallback={<BrandedLoader variant="dashboard" />}>
                <ComparePage />
              </Suspense>
            } />
            <Route path="/payment-success" element={
              <Suspense fallback={<BrandedLoader variant="dashboard" />}>
                <PaymentSuccessRedirect />
              </Suspense>
            } />
            {/* Universal Link handlers for mobile apps */}
            <Route path="/app/payment-success" element={
              <Suspense fallback={<BrandedLoader variant="dashboard" />}>
                <AppPaymentSuccess />
              </Suspense>
            } />
            <Route path="/app/payment-cancelled" element={
              <Suspense fallback={<BrandedLoader variant="dashboard" />}>
                <AppPaymentCancelled />
              </Suspense>
            } />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            {/* /privacy and /terms are the paths the signup flows and email CTAs
                already link to; they used to fall through to the 404. One policy,
                two paths (canonical stays /privacy-policy). */}
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/disclosures" element={<Disclosures />} />
            <Route path="/apps/casework/privacy" element={<CaseworkPrivacy />} />
            <Route path="/apps/casework/support" element={<CaseworkSupport />} />
            {/* Declared in the Play Console Data safety form; must stay
                reachable WITHOUT auth (an email-link visitor is rarely signed
                in) and must keep this exact path. */}
            <Route path="/delete-account" element={<DeleteAccount />} />
            {/* Unified account-LAST onboarding (public: pre-account plan → account).
                Both entry doors (report CTA, deep links) land here. See
                docs/product/unified-onboarding-account-last-spec.md. */}
            <Route path="/onboarding" element={<OnboardingPage />} />
            {/* The exam_date_ask letter's standalone screen; signs in by its own link. */}
            <Route path="/exam-date" element={
              <Suspense fallback={<BrandedLoader variant="dashboard" />}>
                <ExamDatePage />
              </Suspense>
            } />
            {/* The post-account personalize step (name / exam date / study goal),
                reached from Settings "Update Profile". Kept private + unchanged;
                moved off /onboarding, which the public flow now owns. Retired in
                the spec's P2 when its screens fold into /onboarding. */}
            <Route path="/onboarding/edit" element={
              <PrivateRoute>
                <Suspense fallback={<BrandedLoader variant="dashboard" />}>
                  <Onboarding />
                </Suspense>
              </PrivateRoute>
            } />
            <Route path="/settings" element={
              <PrivateRoute>
                <Suspense fallback={<BrandedLoader variant="dashboard" />}>
                  <UserSettings />
                </Suspense>
              </PrivateRoute>
            } />
            <Route path="/dashboard" element={
              <PrivateRoute>
                <Suspense fallback={<BrandedLoader variant="dashboard" />}>
                  <SmartAppBanner />
                  <Dashboard />
                </Suspense>
              </PrivateRoute>
            } />
            {import.meta.env.VITE_TRIAL_WEEK_FIXTURE === 'true' && (
              <Route path="/_trial-preview" element={
                <Suspense fallback={<BrandedLoader variant="dashboard" />}>
                  <TrialDashboardPreviewPage />
                </Suspense>
              } />
            )}
            <Route path="/ask-pam" element={
              <PrivateRoute>
                <Suspense fallback={<BrandedLoader variant="dashboard" />}>
                  <AskPam />
                </Suspense>
              </PrivateRoute>
            } />
            <Route path="/sims" element={
              <PrivateRoute>
                <Suspense fallback={<BrandedLoader variant="dashboard" />}>
                  <SimScenarioSelection />
                </Suspense>
              </PrivateRoute>
            } />
            <Route path="/sims/:scenarioId" element={
              <PrivateRoute>
                <Suspense fallback={<BrandedLoader variant="dashboard" />}>
                  <SimSession />
                </Suspense>
              </PrivateRoute>
            } />
            <Route path="/flashcards" element={
              <PrivateRoute>
                <Suspense fallback={<BrandedLoader variant="dashboard" />}>
                  <FlashcardsPage />
                </Suspense>
              </PrivateRoute>
            } />
            <Route path="/progress" element={
              <PrivateRoute>
                <Suspense fallback={<BrandedLoader variant="dashboard" />}>
                  <ProgressPage />
                </Suspense>
              </PrivateRoute>
            } />
            <Route path="/exam-preparedness" element={
              <Suspense fallback={<BrandedLoader variant="dashboard" />}>
                <ExamPreparednessPage />
              </Suspense>
            } />
            {/* Exam Prep is demoted out of the app nav → point the short alias at Practice */}
            <Route path="/exam-prep" element={<Navigate to="/practice" replace />} />
            <Route path="/practice" element={
              <PrivateRoute>
                <Suspense fallback={<BrandedLoader variant="dashboard" />}>
                  <Practice />
                </Suspense>
              </PrivateRoute>
            } />
            <Route path="/admin" element={
              <PrivateRoute>
                <Suspense fallback={<BrandedLoader variant="dashboard" />}>
                  <AdminDashboard />
                </Suspense>
              </PrivateRoute>
            } />
            <Route path="/admin/review/:batchId" element={
              <PrivateRoute>
                <Suspense fallback={<BrandedLoader variant="dashboard" />}>
                  <ReviewBatchPage />
                </Suspense>
              </PrivateRoute>
            } />
            {/* Affiliate Program Routes */}
            <Route path="/affiliate/signup" element={<AffiliateSignup />} />
            <Route path="/affiliate/signin" element={<AffiliateSignIn />} />
            <Route path="/affiliate/dashboard" element={
              <PrivateRoute>
                <Suspense fallback={<BrandedLoader variant="dashboard" />}>
                  <AffiliateDashboard />
                </Suspense>
              </PrivateRoute>
            } />
            {/* Replace the inline 404 with the new NotFound component */}
            <Route path="*" element={<NotFound />} />
          </Routes>
      </AppChrome>

      {/* Development Student View Panel */}
      {isDevMode && (
        <DevStudentView
          onActivateStudentView={activateStudentView}
          isActive={isStudentViewActive}
          onDeactivate={deactivateStudentView}
        />
      )}
      
      {isProductionHost() && <SpeedInsights />}

      {/* Global notification system powered by Zustand */}
      <NotificationSystem />

      {/* Global toast host for react-hot-toast (VerifyEmailBanner, quiz actions, etc.) */}
      <Toaster position="top-center" toastOptions={{ duration: 4000 }} />
      <ReplayConsentBar />
    </AuthLoadingWrapper>
  );
};

// Wrapper to conditionally show React Query Devtools (Ctrl+Shift+D to toggle)
const DevToolsWrapper = () => {
  const { showDevTools } = useDevTools();
  if (!showDevTools) return null;
  return <ReactQueryDevtools initialIsOpen={false} buttonPosition="top-left" />;
};

// Router basename for path-prefixed deployments. Vite bakes `--base` into
// import.meta.env.BASE_URL, so the staging build (`--base=/staging/web/`) ships an
// app whose asset URLs carry the prefix. Caddy STRIPS `/staging/web` before nginx
// sees the request, but the BROWSER's location.pathname still has it, so react-router
// must be told about it or every route 404s to the SPA fallback. React Router treats
// "/" as "no basename", and BASE_URL is "/" for the production build, so this is a
// no-op outside the staging container.
const ROUTER_BASENAME = import.meta.env.BASE_URL?.replace(/\/$/, '') || '/';

function App() {
  useEffect(() => {
    initializeErrorTracking();
    // Capture UTM-based email attribution on first load
    try { captureEmailAttribution(); } catch (_e) { void 0; }
    // Capture full UTM attribution (any source) for revenue attribution at checkout
    try { captureAttributionIfPresent(); } catch (_e) { void 0; }
    try { rememberAdDoor(window.location.pathname, window.location.search); } catch (_e) { void 0; }
    // Capture affiliate referral code from URL if present
    try { captureReferralCode(); } catch (_e) { void 0; }

    // Clean up expired localStorage items on app startup (non-blocking)
    // Use requestIdleCallback to avoid blocking UI on slow devices
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        try { clearExpiredItems(); } catch (_e) { void 0; }
      });
    } else {
      // Fallback for browsers without requestIdleCallback (Safari)
      setTimeout(() => {
        try { clearExpiredItems(); } catch (_e) { void 0; }
      }, 1000);
    }
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Router basename={ROUTER_BASENAME}>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </Router>
        {import.meta.env.DEV && <DevToolsWrapper />}
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
